import { GoogleMap, Circle, Polygon } from "@react-google-maps/api";
import { useRef, useEffect, useCallback } from "react";
import { DriverLocation } from "@/hooks/useDriverLocation";
import { DriverWithLocation } from "./DriverInfoWindow";

interface ServiceZone {
  id: number;
  coordinates: {
    type: string;
    coordinates: Array<{ lat: number; lon: number }>;
  };
}

interface Driver {
  id: number;
  name: string;
  status: string;
  location_lat: number;
  location_lon: number;
  image?: string;
  driver_working: boolean;
}

interface Branch {
  id: number;
  name: string;
  location_lat: number;
  location_lon: number;
  created_at: string;
  service_zone: ServiceZone;
  drivers?: Driver[];
}

interface MapComponentProps {
  center: { lat: number; lng: number };
  selectedBranch: Branch | null;
  selectedBrandName: string;
  selectedBrandId: string;
  drivers: Driver[];
  realTimeDrivers: { [key: string]: DriverLocation };
  showDrivers: boolean;
  showServiceZone: boolean;
  branchIcon: google.maps.Icon | null;
  driverIcon: google.maps.Icon | null;
  availableDriverIcon: google.maps.Icon | null;
  info: {
    type: "branch" | "driver";
    data: any;
  } | null;
  onBranchClick: (branch: Branch) => void;
  onDriverClick: (driverData: any) => void;
  onInfoWindowClose: () => void;
  onProfileClick: (driver: DriverWithLocation) => void;
  onOrderClick: (orderId: string) => void;
  onNavigateDriver: (direction: "prev" | "next") => void;
}

const containerStyle = { width: "100%", height: "100%" };
const mapGreyStyle = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ saturation: -100 }, { lightness: 30 }],
  },
];
export const DRIVER_STATUS_MAP: Record<
  string,
  {
    dot: string;
    badge: string;
    text: string;
    showOrderButton?: boolean;
  }
> = {
  "no-status": {
    dot: "bg-gray-500",
    badge: "bg-gray-100 text-gray-800",
    text: "Desconectado",
    showOrderButton: false,
  },
  offline: {
    dot: "bg-green-500",
    badge: "bg-green-100 text-green-800",
    text: "Conectado",
    showOrderButton: false,
  },
  available: {
    dot: "bg-green-500",
    badge: "bg-green-100 text-green-800",
    text: "Disponible",
    showOrderButton: false,
  },
  driver_at_branch: {
    dot: "bg-blue-500",
    badge: "bg-blue-100 text-blue-800",
    text: "En sucursal",
    showOrderButton: false,
  },
  on_delivery: {
    dot: "bg-indigo-500",
    badge: "bg-indigo-100 text-indigo-800",
    text: "En ruta",
    showOrderButton: true,
  },
  on_site: {
    dot: "bg-indigo-500",
    badge: "bg-indigo-100 text-indigo-800",
    text: "En sitio",
    showOrderButton: true,
  },
};
const isValidCoordinate = (lat?: number, lng?: number) =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180 &&
  !(lat === 0 && lng === 0); // Exclude coordinates (0, 0) as invalid

const processPolygonCoordinates = (
  coordinates: { lat: number; lon: number }[],
): google.maps.LatLngLiteral[] => {
  if (!coordinates) return [];

  if (
    Array.isArray(coordinates) &&
    coordinates.length > 0 &&
    "lat" in coordinates[0] &&
    "lon" in coordinates[0]
  ) {
    return coordinates.map((coord) => ({
      lat: Number(coord.lat),
      lng: Number(coord.lon),
    }));
  }

  return [];
};

// Status mapping for new statuses

export const MapComponent = ({
  center,
  selectedBranch,
  selectedBrandName,
  selectedBrandId,
  drivers,
  realTimeDrivers,
  showDrivers,
  showServiceZone,
  branchIcon,
  driverIcon,
  availableDriverIcon,
  info,
  onBranchClick,
  onDriverClick,
  onInfoWindowClose,
  onProfileClick,
  onOrderClick,
  onNavigateDriver,
}: MapComponentProps) => {
  // Refs for map, markers, and infoWindow
  const mapRef = useRef<google.maps.Map | null>(null);
  const branchMarkerRef = useRef<google.maps.Marker | null>(null);
  const driverMarkersRef = useRef<{ [key: number]: google.maps.Marker }>({});
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const hasValidServiceZone =
    selectedBranch?.service_zone?.coordinates?.coordinates &&
    selectedBranch.service_zone.coordinates.coordinates.length > 0;

  const polygonCoordinates = selectedBranch?.service_zone?.coordinates
    ?.coordinates
    ? processPolygonCoordinates(
        selectedBranch.service_zone.coordinates.coordinates,
      )
    : [];

  // Helper to get status info
  function getDriverStatusInfo(status: string | undefined) {
    if (!status) return DRIVER_STATUS_MAP["no-status"];
    return DRIVER_STATUS_MAP[status] || DRIVER_STATUS_MAP["offline"];
  }

  // Create InfoWindow content for drivers
  const createDriverInfoWindowContent = useCallback(
    (driverData: any) => {
      if (driverData.drivers) {
        // Multiple drivers at same location
        const driver = driverData.drivers[driverData.index];
        const realTimeData = realTimeDrivers[driver.id];
        const statusInfo = getDriverStatusInfo(realTimeData?.driver_status);

        return `
        <div class="px-1 pb-3 max-w-xs bg-white rounded-lg shadow-lg">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-3">
              <div class="relative">
                <div class="w-10 h-10 rounded-full overflow-hidden bg-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                  ${
                    driver.profilePicture
                      ? `<img src="${driver.profilePicture}" alt="${driver.name || `Conductor ${driver.id}`}" class="w-full h-full object-cover" />`
                      : `<img src="/driver-illustration/profile-pic-default.svg" alt="${driver.name || `Conductor ${driver.id}`}" class="w-full h-full object-cover" />`
                  }
                </div>
                <div class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${statusInfo.dot}"></div>
              </div>
              <div class="flex-1">
                <div class="flex flex-col">
                  <h3 class="font-semibold text-gray-900 text-base leading-tight">${driver.name || `Conductor ${driver.id}`}</h3>
                  <span class="px-2 py-1 text-xs font-medium rounded-full ${statusInfo.badge} mt-1 self-start">
                    ${statusInfo.text}
                  </span>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1">
              <button onclick="window.navigateDriver?.('prev')" class="text-gray-400 hover:text-gray-600 text-sm px-1 py-1 rounded hover:bg-gray-100">‹</button>
              <span class="text-xs text-gray-500 px-1">${driverData.index + 1}/${driverData.drivers.length}</span>
              <button onclick="window.navigateDriver?.('next')" class="text-gray-400 hover:text-gray-600 text-sm px-1 py-1 rounded hover:bg-gray-100">›</button>
            </div>
          </div>
          <div class="flex gap-2 pt-2 border-t border-gray-100">
            <button onclick="window.handleProfileClick?.(${JSON.stringify({ ...driver, ...realTimeData, currentLocation: driver.currentLocation }).replace(/"/g, "&quot;")})" class="flex-1 text-blue-600 hover:text-blue-800 text-sm font-medium py-1 px-2 rounded hover:bg-blue-50 transition-colors">
              Ver perfil
            </button>
            ${
              realTimeData?.orderIds &&
              realTimeData?.orderIds.length > 0 &&
              statusInfo.showOrderButton
                ? `<button onclick="window.handleOrderClick?.(${JSON.stringify({ orderIds: realTimeData.orderIds, realTime: realTimeData }).replace(/"/g, "&quot;")})" class="flex-1 text-blue-600 hover:text-blue-800 text-sm font-medium py-1 px-2 rounded hover:bg-blue-50 transition-colors">
                Ver orden
              </button>`
                : ""
            }
          </div>
        </div>
      `;
      } else {
        // Single driver - using similar design as multiple drivers
        const realTimeData = realTimeDrivers[driverData.id];
        const statusInfo = getDriverStatusInfo(realTimeData?.driver_status);

        return `
        <div class="px-1 pb-3 max-w-xs bg-white rounded-lg shadow-lg">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-3">
              <div class="relative">
                <div class="w-10 h-10 rounded-full overflow-hidden bg-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                  ${
                    driverData.picture
                      ? `<img src="${driverData.profilePicture}" alt="${driverData.name || `Conductor ${driverData.id}`}" class="w-full h-full object-cover" />`
                      : `<img src="/driver-illustration/profile-pic-default.svg" alt="${driverData.name || `Conductor ${driverData.id}`}" class="w-full h-full object-cover" />`
                  }
                </div>
                <div class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${statusInfo.dot}"></div>
              </div>
              <div class="flex-1">
                <div class="flex flex-col">
                  <h3 class="font-semibold text-gray-900 text-base">${driverData.name || `Conductor ${driverData.id}`}</h3>
                    <span class="px-2 py-1 text-xs font-medium rounded-full ${statusInfo.badge} mt-1 self-start">
                    ${statusInfo.text}
                  </span>
                </div>
             
              </div>
            </div>
          </div>
          <div class="flex gap-2 pt-2 border-t border-gray-100">
            <button onclick="window.handleProfileClick?.(${JSON.stringify({ ...driverData, ...realTimeData }).replace(/"/g, "&quot;")})" class="flex-1 text-blue-600 hover:text-blue-800 text-sm font-medium py-1 px-2 rounded hover:bg-blue-50 transition-colors">
              Ver perfil
            </button>
            ${
              realTimeData?.orderIds && realTimeData?.orderIds.length > 0
                ? `<button onclick="window.handleOrderClick?.(${JSON.stringify({ orderIds: realTimeData.orderIds, realTime: realTimeData }).replace(/"/g, "&quot;")})" class="flex-1 text-blue-600 hover:text-blue-800 text-sm font-medium py-1 px-2 rounded hover:bg-blue-50 transition-colors">
                ${realTimeData?.orderIds.length > 1 ? "Ver órdenes" : "Ver orden"}
              </button>`
                : ""
            }
          </div>
        </div>
      `;
      }
    },
    [realTimeDrivers],
  );

  // Create InfoWindow content for branch
  const createBranchInfoWindowContent = useCallback(
    (branchData: Branch) => {
      return `
        <div class="px-1 pb-3 max-w-xs bg-white rounded-lg shadow-lg">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-3">
        
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <h3 class="font-semibold text-gray-900 text-base">${branchData.name}</h3>
                  <span class="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    Activa
                  </span>
                </div>
                <div class="flex items-center gap-1 text-sm text-gray-500">
                  <span>Sucursal de ${selectedBrandName}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="flex gap-2 pt-2 border-t border-gray-100">
            <a href="/brands/${selectedBrandId}/branches/${branchData.id}" class="flex-1 text-blue-600 hover:text-blue-800 text-sm font-medium py-1 px-2 rounded hover:bg-blue-50 transition-colors text-center">
              Ver sucursal
            </a>
          </div>
        </div>
      `;
    },
    [selectedBrandId, selectedBrandName],
  );

  // --- FIX: Always show branch marker and driver markers for current branch ---

  // Helper to create or update the branch marker
  const ensureBranchMarker = useCallback(() => {
    if (!mapRef.current || !selectedBranch || !branchIcon) return;

    // If marker doesn't exist, create it
    if (!branchMarkerRef.current) {
      const branchMarker = new window.google.maps.Marker({
        position: center,
        map: mapRef.current,
        icon: branchIcon,
        title: selectedBranch.name,
      });

      branchMarkerRef.current = branchMarker;
    } else {
      // If marker exists, update its position, icon, and visibility
      branchMarkerRef.current.setPosition(center);
      branchMarkerRef.current.setIcon(branchIcon);
      branchMarkerRef.current.setTitle(selectedBranch.name);
      branchMarkerRef.current.setMap(mapRef.current);
      branchMarkerRef.current.setVisible(true);
    }
  }, [center, selectedBranch, branchIcon, onBranchClick]);

  // Helper to clear branch marker
  const clearBranchMarker = useCallback(() => {
    if (branchMarkerRef.current) {
      branchMarkerRef.current.setMap(null);
      branchMarkerRef.current = null;
    }
  }, []);

  // Helper to create driver markers for current branch
  const ensureDriverMarkers = useCallback(() => {
    // Always clear existing driver markers first
    Object.values(driverMarkersRef.current).forEach((marker) => {
      marker.setMap(null);
    });
    driverMarkersRef.current = {};

    if (!mapRef.current || !showDrivers || !Array.isArray(drivers)) {
      return;
    }

    // Create new driver markers
    drivers.forEach((driver) => {
      if (!driver) return;

      const realTimeData = realTimeDrivers[driver.id];
      const location = realTimeData
        ? { lat: realTimeData.lat, lng: realTimeData.lng }
        : { lat: driver.location_lat, lng: driver.location_lon };

      if (!isValidCoordinate(location.lat, location.lng)) {
        return;
      }

      // Determine icon and color filter
      const markerIcon =
        realTimeData && realTimeData?.driver_status && !driver.driver_working
          ? realTimeData?.driver_status === "on_delivery" ||
            realTimeData?.driver_status === "on_site" ||
            (realTimeData?.orderIds && realTimeData?.orderIds.length > 0)
            ? "/driver-map.png"
            : "/driver-free.png"
          : "/driver-gray.png";

      // Create custom icon configuration
      const iconConfig = markerIcon
        ? {
            url: markerIcon,
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20),
          }
        : {
            url: markerIcon,
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20),
          };

      const marker = new window.google.maps.Marker({
        position: location,
        map: mapRef.current,
        icon: iconConfig,
        title: `Conductor ${driver.name || driver.id}`,
      });

      // Apply color filter based on status
      setTimeout(() => {
        // Create a unique identifier for this marker
        const markerId = `driver-marker-${driver.id}`;

        // Find the marker image element and add a unique class
        const markerImages = document.querySelectorAll(
          `img[src="${markerIcon}"]`,
        ) as NodeListOf<HTMLImageElement>;

        markerImages.forEach((img) => {
          if (!img.classList.contains("driver-marker-processed")) {
            img.classList.add("driver-marker-processed");
            img.classList.add(markerId);
            return;
          }
        });
      }, 300);

      // Add click listener
      marker.addListener("click", () => {
        // Find all drivers at this location (rounded to 5 decimals)
        const driversAtLocation = drivers.filter((d) => {
          const rt = realTimeDrivers[d.id];
          const loc = rt
            ? { lat: rt.lat, lng: rt.lng }
            : { lat: d.location_lat, lng: d.location_lon };
          return (
            Number(loc.lat).toFixed(5) === Number(location.lat).toFixed(5) &&
            Number(loc.lng).toFixed(5) === Number(location.lng).toFixed(5)
          );
        });

        if (driversAtLocation.length > 1) {
          // Multiple drivers at same location
          const driverData = {
            type: "driver" as const,
            data: {
              drivers: driversAtLocation.map((d) => {
                const realTimeData = realTimeDrivers[d.id];
                return {
                  ...d, // Keep all original driver properties including driver_working
                  ...(realTimeData && {
                    lat: realTimeData.lat,
                    lng: realTimeData.lng,
                    driver_status: realTimeData.driver_status,
                    battery: realTimeData.battery,
                    speed: realTimeData.speed,
                    heading: realTimeData.heading,
                    orderIds: realTimeData.orderIds,
                  }),
                  currentLocation: realTimeData
                    ? {
                        lat: realTimeData.lat,
                        lng: realTimeData.lng,
                      }
                    : { lat: d.location_lat, lng: d.location_lon },
                };
              }),
              index: driversAtLocation.findIndex((d) => d.id === driver.id),
            },
          };
          onDriverClick(driverData);
        } else {
          // Single driver
          const driverData = {
            type: "driver" as const,
            data: {
              ...driver, // Keep all original driver properties including driver_working
              ...(realTimeData && {
                lat: realTimeData.lat,
                lng: realTimeData.lng,
                driver_status: realTimeData.driver_status,
                battery: realTimeData.battery,
                speed: realTimeData.speed,
                heading: realTimeData.heading,
                orderIds: realTimeData.orderIds,
              }),
              currentLocation: location,
            },
          };
          onDriverClick(driverData);
        }
      });

      driverMarkersRef.current[driver.id] = marker;
    });
  }, [
    drivers,
    realTimeDrivers,
    showDrivers,
    driverIcon,
    availableDriverIcon,
    onDriverClick,
  ]);

  // Helper to clear driver markers
  const clearDriverMarkers = useCallback(() => {
    Object.values(driverMarkersRef.current).forEach((marker) => {
      marker.setMap(null);
    });
    driverMarkersRef.current = {};
  }, []);

  // --- END FIX ---

  // Always ensure branch marker is present and visible
  useEffect(() => {
    if (selectedBranch && branchIcon && mapRef.current) {
      ensureBranchMarker();
    } else {
      clearBranchMarker();
    }
  }, [selectedBranch, branchIcon, center.lat, center.lng, mapRef.current]);

  // Always ensure driver markers are present and visible
  useEffect(() => {
    if (showDrivers && mapRef.current) {
      ensureDriverMarkers();
    } else {
      clearDriverMarkers();
    }
  }, [drivers, realTimeDrivers, showDrivers, mapRef.current]);

  // Update InfoWindow when info changes
  useEffect(() => {
    if (!mapRef.current || !infoWindowRef.current) return;

    if (info && info.data) {
      let position;
      let content;

      if (info.type === "branch") {
        const { location_lat, location_lon } = info.data;
        if (!location_lat || !location_lon) return;

        position = { lat: location_lat, lng: location_lon };

        content = createBranchInfoWindowContent(info.data);
      } else if (info.data.drivers) {
        const driver = info.data.drivers[info.data.index];
        if (!driver?.currentLocation) return;

        position = {
          lat: driver.currentLocation.lat,
          lng: driver.currentLocation.lng,
        };
        content = createDriverInfoWindowContent(info.data);
      } else {
        const { currentLocation } = info.data;
        if (!currentLocation) return;

        position = {
          lat: currentLocation.lat,
          lng: currentLocation.lng,
        };
        content = createDriverInfoWindowContent(info.data);
      }

      infoWindowRef.current.setContent(content);
      infoWindowRef.current.setPosition(position);
      infoWindowRef.current.open(mapRef.current);
    } else {
      infoWindowRef.current.close();
    }
  }, [info, createDriverInfoWindowContent, createBranchInfoWindowContent]);

  // Set up global functions for InfoWindow interactions
  useEffect(() => {
    (window as any).handleProfileClick = onProfileClick;
    (window as any).handleOrderClick = onOrderClick;
    (window as any).navigateDriver = onNavigateDriver;

    return () => {
      delete (window as any).handleProfileClick;
      delete (window as any).handleOrderClick;
      delete (window as any).navigateDriver;
    };
  }, [onProfileClick, onOrderClick, onNavigateDriver]);

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={14}
      options={{
        styles: mapGreyStyle,
        zoomControl: true,
        streetViewControl: true,
        mapTypeControl: true,
        fullscreenControl: true,
      }}
      onLoad={(map) => {
        mapRef.current = map;

        // Create InfoWindow
        const infoWindow = new window.google.maps.InfoWindow({
          maxWidth: 300,
        });

        infoWindow.addListener("closeclick", () => {
          onInfoWindowClose();
        });

        infoWindowRef.current = infoWindow;

        // Always ensure branch marker is present and visible
        if (selectedBranch && branchIcon) {
          ensureBranchMarker();
        }
        // Always ensure driver markers are present and visible
        if (showDrivers) {
          ensureDriverMarkers();
        }
      }}
      onUnmount={() => {
        // Clean up markers when map unmounts
        clearBranchMarker();
        clearDriverMarkers();
      }}
    >
      {showServiceZone && selectedBranch && (
        <>
          <Circle
            center={center}
            radius={200}
            options={{
              strokeColor: "#8e24aa",
              fillColor: "#8e24aa",
              fillOpacity: 0.2,
            }}
          />
          {hasValidServiceZone && polygonCoordinates.length > 0 && (
            <Polygon
              paths={polygonCoordinates}
              options={{
                strokeColor: "#4CAF50",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#4CAF50",
                fillOpacity: 0.35,
              }}
            />
          )}
        </>
      )}
    </GoogleMap>
  );
};

export type { Branch, Driver, ServiceZone };
